/**
 * ── THE DRAWN-AREA FILTER (VRG-480) ─────────────────────────────────────────────────
 *
 * A ring of coordinates the reader drew on a scan map. It filters scans by their OWN
 * `geo_lat` / `geo_lng`, so it is a coordinate predicate and nothing more: it never
 * resolves to a country, a city, or any named place.
 *
 * That is what makes it compose with the existing `location` filter without a conflict
 * rule. `location` constrains `scans.geo_country`; this constrains the same row's
 * coordinates. Two predicates over two columns AND together, and the pins on the map
 * already reflect the country filter — so drawing narrows THAT set rather than replacing
 * it. There is no "does it replace or combine" question to answer.
 *
 * This module owns the ring's numeric lifecycle: simplify, cap, encode, decode — plus
 * `pointInRing`, the CLIENT-SIDE membership test (see its own header for when that is the
 * right tool and when it is not).
 *
 * ## Why this lives in `utils/`, not `store/analytics/`
 *
 * It was `store/analytics/area.ts` until VRG-491, when the certificate page's Scans tab
 * gained the same draw-to-filter gesture. Nothing in here is analytics-specific or
 * redux-aware — it is arithmetic over a list of coordinates — and leaving it under
 * `store/analytics/` would have forced the certificate page either to import from another
 * page's redux slice or, far more likely, to write its own second copy of Douglas-Peucker
 * and the vertex cap. Two copies of a simplification tolerance is exactly the drift
 * working-conventions §36 exists to prevent, and it would be invisible: both copies would
 * "work", and the two surfaces would silently disagree about which gestures are a valid
 * ring.
 *
 * The server owns its own predicate (`apps/api/src/domain/types/geo-ring.ts`), and the
 * two agree on exactly one thing — the wire format below — which is why that format is
 * stated in both files and nowhere else.
 */

export type GeoPoint = { lat: number; lng: number }

/**
 * The vertex cap, AFTER simplification.
 *
 * ⚠️ Must equal `MAX_AREA_VERTICES` in `apps/api/src/domain/types/geo-ring.ts`. The
 * server rejects a longer ring with a 400 rather than trimming it, so a client cap above
 * the server's would turn a legitimate gesture into a failed request.
 *
 * 50 is generous. A freehand loop emits several hundred raw points; the ones that
 * survive Douglas-Peucker at a tolerance the eye cannot resolve are typically 15-30. The
 * cap exists to bound two things that are not the query — the query cost is bounded by
 * the bounding-box prefilter — but the REQUEST size and, more importantly, the SAVED VIEW
 * payload, which lives in a user's preferences blob alongside every other view.
 */
export const MAX_AREA_VERTICES = 50

/** Coordinate precision on the wire. 5dp ≈ 1.1 m, far finer than any hand gesture. */
const WIRE_DP = 5

/**
 * Perpendicular distance from `p` to the segment `a`-`b`, in degrees.
 *
 * DEGREES, not metres, and deliberately so. Converting to metres would need a cos(lat)
 * correction that varies along a large ring, and the tolerance this feeds is derived from
 * the ring's own extent (see `simplifyRing`) rather than from an absolute distance — so
 * the units cancel. A metre-accurate distance would buy nothing and would make the
 * tolerance behave differently at different latitudes for no visible benefit.
 */
const perpendicularDistance = (p: GeoPoint, a: GeoPoint, b: GeoPoint): number => {
  const dx = b.lng - a.lng
  const dy = b.lat - a.lat
  if (dx === 0 && dy === 0) return Math.hypot(p.lng - a.lng, p.lat - a.lat)
  // Distance from a point to an infinite line through a and b (the standard
  // cross-product form). Segment endpoints are always retained by the recursion below,
  // so the infinite-line form is the right one here.
  return Math.abs(dy * p.lng - dx * p.lat + b.lng * a.lat - b.lat * a.lng) / Math.hypot(dx, dy)
}

/** Douglas-Peucker over an open polyline. Endpoints are always kept. */
const douglasPeucker = (points: GeoPoint[], tolerance: number): GeoPoint[] => {
  if (points.length < 3) return points

  let maxDistance = 0
  let index = 0
  const first = points[0]
  const last = points[points.length - 1]

  for (let i = 1; i < points.length - 1; i += 1) {
    const distance = perpendicularDistance(points[i], first, last)
    if (distance > maxDistance) {
      maxDistance = distance
      index = i
    }
  }

  if (maxDistance <= tolerance) return [first, last]

  const left = douglasPeucker(points.slice(0, index + 1), tolerance)
  const right = douglasPeucker(points.slice(index), tolerance)
  // `right[0]` is `left[left.length - 1]` — the split vertex, kept once.
  return [...left.slice(0, -1), ...right]
}

/** Drop consecutive points that round to the same wire coordinate. */
const dedupe = (points: GeoPoint[]): GeoPoint[] => {
  const out: GeoPoint[] = []
  for (const p of points) {
    const previous = out[out.length - 1]
    if (previous && previous.lat.toFixed(WIRE_DP) === p.lat.toFixed(WIRE_DP) && previous.lng.toFixed(WIRE_DP) === p.lng.toFixed(WIRE_DP)) {
      continue
    }
    out.push(p)
  }
  return out
}

/**
 * Reduce a raw gesture to a ring at or under `MAX_AREA_VERTICES`, or `null` if it cannot
 * be reduced that far without becoming a different shape.
 *
 * ## Why the tolerance escalates instead of being a constant
 *
 * A single tolerance in degrees means one thing at world zoom and something else entirely
 * at street zoom: 0.001° is ~110 m, which is invisible across a continent and destroys a
 * shape drawn around a warehouse. Deriving the starting tolerance from the ring's OWN
 * extent makes it scale-free — 0.2% of the larger dimension is below the width of the
 * stroke the reader drew, at any zoom.
 *
 * The loop then escalates only if the first pass is still over the cap, which happens for
 * a deliberately wiggly gesture. Each round multiplies by 1.6, so twenty rounds cover a
 * factor of ~12,000 — far past the point where any ring collapses to a triangle.
 *
 * ## Why failure returns null rather than a truncated ring
 *
 * ⚠️ A truncated ring is a DIFFERENT AREA, silently. Dropping vertex 51 onward does not
 * approximate the shape, it re-closes the polygon across whatever chord is left, and the
 * user would see a filter applied to a region they did not draw with nothing on screen
 * saying so. The caller's contract is therefore "no ring" rather than "some ring", which
 * is a visible outcome the reader can react to.
 *
 * In practice this branch is unreachable — twenty escalations reduce any polyline to two
 * points — and it is here as a guarantee rather than a handled case.
 */
export const prepareRing = (raw: GeoPoint[]): GeoPoint[] | null => {
  const points = dedupe(raw.filter(p => Number.isFinite(p.lat) && Number.isFinite(p.lng)))
  // Two points describe a line segment, which encloses nothing. A filter that can never
  // match is worse than no filter, so a stray click or a flick produces neither.
  if (points.length < 3) return null

  const lats = points.map(p => p.lat)
  const lngs = points.map(p => p.lng)
  const extent = Math.max(Math.max(...lats) - Math.min(...lats), Math.max(...lngs) - Math.min(...lngs))
  // A ring with no extent at all (every point identical after dedupe is impossible, but
  // a hairline scribble is not) has nothing to enclose.
  if (extent <= 0) return null

  let tolerance = extent * 0.002
  for (let attempt = 0; attempt < 20; attempt += 1) {
    const simplified = dedupe(douglasPeucker(points, tolerance))
    if (simplified.length < 3) return null
    if (simplified.length <= MAX_AREA_VERTICES) return simplified
    tolerance *= 1.6
  }
  return null
}

/**
 * The wire form: `lat,lng;lat,lng;…`, implicitly closed.
 *
 * A single scalar rather than a nested array because the request goes through axios'
 * default query serialiser, where an array of pairs has no unambiguous encoding — the
 * server would have to guess at one, and a guess in a filter parser is how a filter comes
 * to mean something other than what was drawn.
 *
 * `Number(toFixed())` trims trailing zeros, so `48.85` stays four characters rather than
 * becoming `48.850000`. Across 50 vertices that is the difference between ~1,050 and
 * ~1,600 characters of query string.
 */
export const encodeRing = (ring: GeoPoint[] | null | undefined): string | undefined => {
  if (!ring || ring.length < 3) return undefined
  return ring
    .map(p => `${Number(p.lat.toFixed(WIRE_DP))},${Number(p.lng.toFixed(WIRE_DP))}`)
    .join(';')
}

/**
 * Parse a persisted ring back into points. Returns `null` for anything malformed, out of
 * range, or over the cap.
 *
 * ⚠️ Used on the SAVED-VIEW restore path, where the input is a preferences blob that may
 * predate this feature or have been written by a different client version. Returning null
 * there means the view restores with no area filter — which is correct, because the
 * alternative (restoring a partially-parsed ring) would narrow every figure on the page by
 * an amount nobody chose.
 */
export const decodeRing = (encoded: string | null | undefined): GeoPoint[] | null => {
  if (!encoded) return null

  const pairs = encoded.split(';')
  if (pairs.length < 3 || pairs.length > MAX_AREA_VERTICES) return null

  const ring: GeoPoint[] = []
  for (const pair of pairs) {
    const [latText, lngText] = pair.split(',')
    const lat = Number(latText)
    const lng = Number(lngText)
    if (latText === undefined || lngText === undefined) return null
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null
    ring.push({ lat, lng })
  }
  return ring
}

/**
 * `true` when the two rings describe the same area on the wire.
 *
 * Compares the ENCODED forms, not the objects, so a ring restored from a saved view
 * compares equal to the one that was saved (the round trip through 5dp is lossy in the
 * last bits of a float, and an object-identity or epsilon comparison would report a
 * difference the user has no way to see). This is what lets a freshly-applied view stop
 * reporting itself as modified — the same reason `useAnalyticsView` omits empty keys.
 */
export const ringsEqual = (a: GeoPoint[] | null, b: GeoPoint[] | null): boolean =>
  encodeRing(a) === encodeRing(b)

/**
 * `true` when `point` falls inside `ring` — the CLIENT-SIDE half of the drawn-area
 * predicate. Added VRG-491.
 *
 * ## When to use this, and when not to
 *
 * ⚠️ The analytics page does NOT call this, and should not start. Its map is one card
 * among many that all narrow to the same `filters.area`, so the ring belongs in the
 * REQUEST (`withAnalyticsArea` → `encodeRing`) where one predicate can constrain every
 * figure on the page at once, and the server evaluates it in SQL over rows the client has
 * never seen. Evaluating it here instead would filter only the page of points already
 * fetched, and every other card would disagree with the map.
 *
 * This exists for the opposite shape: the certificate page's Scans tab, which loads ONE
 * certificate's scans in full and does all of its search, status and sort filtering on the
 * client precisely because that set is small. There the ring is one more client-side
 * predicate among several, and round-tripping it to the server would make the map's own
 * filter the only one that costs a request.
 *
 * ## It mirrors `buildGeoRingFilter`'s algorithm deliberately, including the ugly parts
 *
 * The server's SQL (`apps/api/src/domain/types/geo-ring.ts`) is the reference
 * implementation, and this is the same two stages in the same order — a bounding-box
 * rejection, then the crossing-number (ray-casting) test — so a ring drawn on one surface
 * selects the same scans it would on the other. Two membership tests that disagree at the
 * boundary would be a bug nobody could see: both answers look plausible, and the only
 * symptom is a pin the list does not have.
 *
 * The edge walk here pairs each vertex with its PREDECESSOR where the SQL pairs it with
 * its successor. That is the same edge set traversed the other way round, and the crossing
 * formula is symmetric under swapping a segment's endpoints (the algebra cancels), so the
 * two produce identical results rather than merely similar ones.
 *
 * ## The antimeridian guard is not optional
 *
 * A ring drawn across the 180th meridian arrives with vertices at +179 and −179, because
 * Google normalises every `LatLng` into [−180, 180]. Read literally that ring spans 358°
 * the WRONG WAY: it describes the whole globe except the strip the reader drew, so the
 * test returns the exact COMPLEMENT of the intended answer. Shifting negative longitudes
 * by +360 when the raw span exceeds 180° — applied to the ring and to the point under
 * test alike — makes the ring contiguous again. Same heuristic, same threshold, and the
 * same honest limit as the server's: it cannot distinguish "crosses the antimeridian"
 * from "genuinely encircles more than half the planet", which is a selection no reader
 * has a reason to draw.
 */
export const pointInRing = (point: GeoPoint, ring: GeoPoint[] | null | undefined): boolean => {
  // Fewer than three vertices encloses nothing. `prepareRing` already refuses to produce
  // such a ring, so this is a guarantee rather than a handled case — but a filter that
  // silently matched EVERYTHING on a malformed ring is the wrong way to fail.
  if (!ring || ring.length < 3) return false
  if (!Number.isFinite(point.lat) || !Number.isFinite(point.lng)) return false

  const rawLngs = ring.map(p => p.lng)
  const unwrapped = Math.max(...rawLngs) - Math.min(...rawLngs) > 180
  const shift = (lng: number) => (unwrapped && lng < 0 ? lng + 360 : lng)

  const lats = ring.map(p => p.lat)
  const lngs = rawLngs.map(shift)
  const py = point.lat
  const px = shift(point.lng)

  // Stage 1 — the bounding box. Cheap, and it is what keeps stage 2 off the points that
  // were never candidates.
  if (py < Math.min(...lats) || py > Math.max(...lats)) return false
  if (px < Math.min(...lngs) || px > Math.max(...lngs)) return false

  // Stage 2 — crossing number. `j` trails `i` by one, wrapping, which is what closes the
  // ring implicitly (the last vertex joins the first).
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const y1 = lats[i]
    const x1 = lngs[i]
    const y2 = lats[j]
    const x2 = lngs[j]
    // Straddle test. It already excludes horizontal edges, but the division below is
    // guarded anyway — JS would hand back ±Infinity rather than throwing, and an
    // Infinity comparison flips `inside` on an edge that should contribute nothing.
    if (y1 > py !== y2 > py) {
      const dy = y2 - y1
      if (dy !== 0 && px < ((x2 - x1) * (py - y1)) / dy + x1) inside = !inside
    }
  }
  return inside
}
